import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Metric } from "../../../../../api/types/portfolioMonitoringTypes";

interface Props {
  metric: Metric;
  onClose: () => void;
  onDeleted: (metric: Metric) => void;
}

const deleteMetric = withErrorHandling(adminApi.deleteMetric);

const DeleteMetricDialog = ({ metric, onClose, onDeleted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleDelete = async () => {
    setSaving(true);
    const [, error] = await deleteMetric(metric.id);
    setSaving(false);

    if (error) {
      logError(error, "[DeleteMetricDialog] deleteMetric");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Metric deleted successfully");
    onDeleted(metric);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete Metric</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Delete metric &apos;{metric.name}&apos;?
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              Existing data submissions that include it may not open correctly, though all previously submitted data
              will be preserved. This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={saving} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteMetricDialog;
